import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import LaunchOutlinedIcon from 'components/material-symbol/icons/Launch'
import ReceiptIcon from 'components/material-symbol/icons/Receipt'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import AddToPhotosIcon from 'components/material-symbol/icons/AddToPhotos'
import PersonIcon from 'components/material-symbol/icons/Person'
import CreditCardIcon from 'components/material-symbol/icons/CreditCard'
import CheckIcon from 'components/material-symbol/icons/Check'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import TonalButton from 'components/empty-page-state/tonal-button'
import { getCoachExternalIdFromProduct } from '../transaction-helpers'
import { useAuth } from 'lib/auth'
import { safeFormat } from 'lib/family-cup-utils/event-dates'
import { useStyles } from './styles'
import {
  byCreatedAt,
  formatCurrency,
  getTransactionReceipt,
} from './transaction-receipt-data'

const monthDayYearFormat = 'MM/dd/yyyy'

const SummaryCard = ({ title, children, headerAction, classes }) => (
  <Box className={classes.card}>
    <Box className={classes.cardHeader}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Typography className={classes.cardTitle}>{title}</Typography>
        {headerAction}
      </Box>
    </Box>
    <Box className={classes.cardDivider} />
    <Box className={classes.cardContent}>{children}</Box>
  </Box>
)

const SummaryField = ({ label, icon: Icon, children, classes }) => (
  <Box className={classes.summaryField}>
    <Typography className={classes.summaryLabel}>{label}</Typography>
    <Box className={classes.summaryValueRow}>
      <Box className={classes.summaryIconWrap}>
        <Icon
          className={classes.summaryIcon}
          fontSize="small"
          opticalSize={20}
        />
      </Box>
      <Box minWidth={0}>{children}</Box>
    </Box>
  </Box>
)

const FeeLineItemsSection = ({ transaction, classes }) => {
  const { lineItems, subtotal } = transaction

  return (
    <Box className={classes.feeSection}>
      {lineItems.map(({ amountInCents, displayLabel }, index) => (
        <Box key={`${displayLabel}-${index}`} className={classes.feeRow}>
          <Typography component="span">{displayLabel}</Typography>
          <Typography component="span">
            {formatCurrency(amountInCents)}
          </Typography>
        </Box>
      ))}
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Subtotal</Typography>
        <Typography component="span">{formatCurrency(subtotal)}</Typography>
      </Box>
    </Box>
  )
}

const OrderTotalSection = ({ totals, classes }) => {
  const { orderTotal, offlineFees, consumerTotal } = totals

  return (
    <>
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Order Total</Typography>
        <Typography component="span">{formatCurrency(orderTotal)}</Typography>
      </Box>
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Offline Fees</Typography>
        <Typography component="span">{formatCurrency(offlineFees)}</Typography>
      </Box>
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Consumer Total</Typography>
        <Typography component="span">
          {formatCurrency(consumerTotal)}
        </Typography>
      </Box>
    </>
  )
}

const TransactionRecordRow = ({ transaction, classes }) => {
  const { amountInCents, createdAt, refund, refundReason } = transaction
  const date = safeFormat(createdAt, monthDayYearFormat)
  const type = refund ? 'Refund' : 'Original Purchase'

  return (
    <Box className={classes.recordRow}>
      <Box minWidth={0}>
        <Box className={classes.recordRowPrimary}>
          <Typography className={classes.recordDate}>{date}</Typography>
          <Typography className={classes.recordAmount}>
            {formatCurrency(amountInCents)}
          </Typography>
        </Box>
        {refund && refundReason ? (
          <Typography className={classes.recordRefundReason}>
            {refundReason}
          </Typography>
        ) : null}
      </Box>
      <Typography className={classes.recordType}>{type}</Typography>
    </Box>
  )
}

const AccountTransactionSummary = ({
  registrationPayment,
  paymentSourceType,
}) => {
  const classes = useStyles()
  const { isCoach } = useAuth()

  const { transactions } = registrationPayment
  const {
    id,
    payorName,
    payorEmail,
    purchaseDate,
    paymentMethod,
    chargeDetails,
    product,
    transactionType,
    lastUpdated,
    initialTransaction,
    feeGroups,
    totals,
  } = getTransactionReceipt(registrationPayment, paymentSourceType)
  const coachExternalId = getCoachExternalIdFromProduct(
    registrationPayment.products[0],
  )

  return (
    <Box className={classes.stack}>
      <SummaryCard title="Transaction Summary" classes={classes}>
        <Box className={classes.summaryGrid}>
          <SummaryField
            label="Transaction Number"
            icon={ReceiptIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>{id}</Typography>
          </SummaryField>
          <SummaryField
            label="Billing Information"
            icon={PersonIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {payorName}
            </Typography>
            <Typography className={classes.summarySecondary}>
              {payorEmail}
            </Typography>
          </SummaryField>
          <SummaryField
            label="Date of Purchase"
            icon={CalendarTodayIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {purchaseDate}
            </Typography>
          </SummaryField>
          <SummaryField
            label="Payment Method"
            icon={CreditCardIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {paymentMethod}
            </Typography>
            {chargeDetails.map(detail => (
              <Typography key={detail} className={classes.summarySecondary}>
                {detail}
              </Typography>
            ))}
          </SummaryField>
          <SummaryField
            label="Product"
            icon={AddToPhotosIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>{product}</Typography>
          </SummaryField>
          <SummaryField
            label="Transaction Type"
            icon={CheckIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {transactionType}
            </Typography>
          </SummaryField>
        </Box>
        <Box className={classes.cardDivider} />
        <Box className={classes.lastUpdatedRow}>
          <ScheduleIcon className={classes.lastUpdatedIcon} fontSize="small" />
          <span>Last Updated: {lastUpdated}</span>
        </Box>
      </SummaryCard>

      {isCoach
        ? feeGroups.map((item, index) => (
          <SummaryCard
            key={`${item.title}-${index}`}
            title={item.title}
            classes={classes}
          >
            <Box className={classes.feesBody}>
              <FeeLineItemsSection transaction={item} classes={classes} />
              {index === feeGroups.length - 1 ? (
                <OrderTotalSection totals={totals} classes={classes} />
              ) : null}
            </Box>
          </SummaryCard>
        ))
        : null}

      <SummaryCard
        title="Transaction Record"
        classes={classes}
        headerAction={
          initialTransaction?.stripeReceiptUrl ? (
            <Button
              component="a"
              href={initialTransaction.stripeReceiptUrl}
              target="_blank"
              rel="noopener"
              className={classes.receiptLink}
              startIcon={
                <LaunchOutlinedIcon fontSize="small" opticalSize={18} />
              }
            >
              View Stripe Receipt
            </Button>
          ) : null
        }
      >
        <Box className={classes.recordStack}>
          {Array.from(transactions)
            .sort(byCreatedAt)
            .map((transaction, index) => (
              <TransactionRecordRow
                key={`${transaction.createdAt}-${index}`}
                transaction={transaction}
                classes={classes}
              />
            ))}
        </Box>
      </SummaryCard>

      {!isCoach && coachExternalId ? (
        <Box className={classes.refundCard}>
          <Box className={classes.refundCopy}>
            <Typography className={classes.refundTitle}>
              Need a refund?
            </Typography>
            <Typography className={classes.refundCaption}>
              If you need a refund, message your coach.
            </Typography>
          </Box>
          <StreamSendMessageButton
            userIds={[coachExternalId]}
            component={TonalButton}
            className={classes.messageCoachButton}
            startIcon={
              <MessageOutlinedIcon
                fontSize="small"
                className={classes.messageCoachButtonIcon}
              />
            }
          >
            Message
          </StreamSendMessageButton>
        </Box>
      ) : null}
    </Box>
  )
}

export default AccountTransactionSummary
